import React from 'react';
import type {AddFormType} from './types';
import type {AgreementClause, DocumentChoice} from '@chekin/core';

type IdentityVerificationDetails = {
  enabled: boolean;
  required: boolean;
  ocr: boolean;
  biomatch: boolean;
  documents: DocumentChoice[];
  isIvExempted: boolean;
  isGuestRegisterBlocked: boolean;
  underageAllowedAge: number | null;
};

export type GuestSchemaStateOptions = {
  reservationId?: string;
  enabled?: boolean;
  onSchemaError?: (error: unknown) => void;
};

export type GuestSchemaContextProps = {
  identityVerificationDetails: IdentityVerificationDetails;
  isGuestSchemaLoaded: boolean;
  isGuestSchemaLoading: boolean;
  isGuestSchemaFetching: boolean;
  isGuestSchemaInitialLoading: boolean;
  hasGuestSchemaFailed: boolean;
  refetchGuestSchema: () => void;
  formFields: AddFormType;
  agreementClauses?: AgreementClause[];
  reservationDocumentsList?: DocumentChoice[];
  setBirthCountry?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setArrivalCountry?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setArrivalDistrict?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setNextDestinationCountry?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setNationality?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDocumentType?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDocumentExpeditionCountry?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setResidenceCountry?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setResidenceProvince?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCitizenship?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setBirthDate?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setHasPhone?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLeaderGuest?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setInvoiceLeaderType?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setRecipientCodeType?: React.Dispatch<React.SetStateAction<string | undefined>>;
};

const GuestSchemaContext = React.createContext<GuestSchemaContextProps>({
  formFields: {},
  isGuestSchemaLoaded: false,
  isGuestSchemaLoading: false,
  isGuestSchemaFetching: false,
  isGuestSchemaInitialLoading: false,
  hasGuestSchemaFailed: false,
  refetchGuestSchema: () => undefined,
  identityVerificationDetails: {
    enabled: false,
    required: false,
    ocr: false,
    biomatch: false,
    documents: [],
    isIvExempted: false,
    isGuestRegisterBlocked: false,
    underageAllowedAge: null,
  },
});

function useGuestSchema() {
  return React.useContext(GuestSchemaContext);
}

export {GuestSchemaContext, useGuestSchema};
export type {IdentityVerificationDetails};
